var _a;
import * as luxon from 'luxon';
import { pipe } from './pipe';
import { chain, chainC, mapC, mapState, mapStateC, combine, combineC, result, TimeBox } from './timebox';
import AppDate from '../AppDate';
import * as timeZonesTs from './timeZones.json';
const timeZones = Array.isArray(timeZonesTs)
    ? timeZonesTs
    : (_a = timeZonesTs.default) !== null && _a !== void 0 ? _a : [];
function isValidTimeZone(tz) {
    const index = timeZones.findIndex((zone) => zone === tz);
    return index !== -1;
}
const legacyTimezoneSupport = (state, timezone) => {
    const tz = timezone ? timezone : state.luxon.DateTime.local().zoneName || 'Europe/Copenhagen';
    if (isValidTimeZone(tz)) {
        return tz;
    }
    else {
        const timezoneOffset = new Date().getTimezoneOffset() / 60;
        switch (tz) {
            case 'America/Nuuk':
                return 'America/Godthab';
            default:
                if (typeof timezoneOffset === 'number') {
                    return timezoneOffset < 0 ? `UTC${timezoneOffset}` : `UTC+${timezoneOffset}`;
                }
                return 'Europe/Copenhagen';
        }
    }
};
export const mkLuxonTimebox = (p) => new LuxonImpl(p);
export const TimeBoxFormats = Object.freeze({
    DD_MM_YYYY: 'dd-MM-yyyy',
    YYYY_MM_DD: 'yyyy-MM-dd',
    HH_mm: 'HH:mm',
    HH: 'HH',
    mm: 'mm',
    HH_mm_ss: 'HH:mm:ss',
    YYYY_MM_DD__HH_mm: 'yyyy-MM-dd HH:mm',
    YYYY_MM_DD__HH_mm_ss: 'yyyy-MM-dd HH:mm:ss',
});
const impl = {
    map: (current, fn) => {
        return mkLuxonTimebox((state) => {
            const [s, v] = current.runBox(state);
            return [state, fn(v)];
        });
    },
    mapC: mapC,
    chain: chain,
    chainC: chainC,
    mapState: mapState,
    mapStateC: mapStateC,
    combine: combine,
    combineC: combineC,
    pipe: pipe,
    cond: (a, predicate) => mkLuxonTimebox((state) => {
        const [s, v] = a.runBox(state);
        return [s, predicate(s, v)];
    }),
    condC: (predicate) => (a) => mkLuxonTimebox((state) => {
        const [s, v] = a.runBox(state);
        return [s, predicate(s, v)];
    }),
    run: (tb) => tb.runBox({ luxon }),
    runWithState: (tb, state) => tb.runBox(Object.assign({}, state)),
    now: () => mkLuxonTimebox((state) => {
        const date = AppDate.getDate();
        const realZone = state.luxon.DateTime.local().zone;
        const m = state.luxon.DateTime.fromJSDate(date).setZone(realZone, {
            keepLocalTime: true,
        });
        return [Object.assign(Object.assign({}, state), { instance: m }), m];
    }),
    realNow: () => mkLuxonTimebox((state) => {
        const m = state.luxon.DateTime.now();
        return [Object.assign(Object.assign({}, state), { instance: m }), m];
    }),
    utcNow: () => mkLuxonTimebox((state) => {
        const m = state.luxon.DateTime.fromJSDate(AppDate.getDate()).toUTC();
        return [Object.assign(Object.assign({}, state), { instance: m }), m];
    }),
    toUtc: (a) => mkLuxonTimebox((state) => {
        const [s, val] = a.runBox(state);
        const instanceUtc = state.instance.toUTC();
        return [Object.assign(Object.assign({}, s), { instance: instanceUtc }), val];
    }),
    unix: (tb) => mkLuxonTimebox((state) => {
        const [newstate, _res] = tb.runBox(state);
        return [newstate, newstate.instance.toMillis()];
    }),
    toJsDate: (a) => {
        return mkLuxonTimebox((state) => {
            const [stateA] = a.runBox(state);
            return [Object.assign({}, state), stateA.instance.toJSDate()];
        });
    },
    toISO: (a) => {
        return mkLuxonTimebox((state) => {
            const [stateA] = a.runBox(state);
            return [Object.assign({}, state), stateA.instance.toISO()];
        });
    },
    fromJsObject: (date) => mkLuxonTimebox((state) => {
        const m = state.luxon.DateTime.fromJSDate(date);
        return [Object.assign(Object.assign({}, state), { instance: m }), m];
    }),
    fromISO: (date) => mkLuxonTimebox((state) => {
        const m = state.luxon.DateTime.fromISO(date);
        return [Object.assign(Object.assign({}, state), { instance: m }), m];
    }),
    fromMillis: (millis) => mkLuxonTimebox((state) => {
        const m = state.luxon.DateTime.fromMillis(millis);
        return [Object.assign(Object.assign({}, state), { instance: m }), m];
    }),
    lt: (a, b) => {
        return mkLuxonTimebox((state) => {
            const [stateA] = a.runBox(state);
            const [stateB] = b.runBox(state);
            return [Object.assign({}, state), stateA.instance < stateB.instance];
        });
    },
    lte: (a, b) => {
        return mkLuxonTimebox((state) => {
            const [stateA] = a.runBox(state);
            const [stateB] = b.runBox(state);
            return [Object.assign({}, state), stateA.instance <= stateB.instance];
        });
    },
    gt: (a, b) => {
        return mkLuxonTimebox((state) => {
            const [stateA] = a.runBox(state);
            const [stateB] = b.runBox(state);
            return [Object.assign({}, state), stateA.instance > stateB.instance];
        });
    },
    gte: (a, b) => {
        return mkLuxonTimebox((state) => {
            const [stateA] = a.runBox(state);
            const [stateB] = b.runBox(state);
            return [Object.assign({}, state), stateA.instance >= stateB.instance];
        });
    },
    eq: (a, b) => {
        return mkLuxonTimebox((state) => {
            const [stateA] = a.runBox(state);
            const [stateB] = b.runBox(state);
            const areSame = [
                'year',
                'years',
                'quarter',
                'quarters',
                'month',
                'months',
                'week',
                'weeks',
                'day',
                'days',
                'hour',
                'hours',
                'minute',
                'minutes',
                'second',
                'seconds',
                'millisecond',
                'milliseconds',
            ].filter((unit) => !stateA.instance.hasSame(stateB.instance, unit));
            return [Object.assign({}, state), areSame.length === 0];
        });
    },
    add: (a, durration) => {
        return mkLuxonTimebox((state) => {
            const [stateA, val] = a.runBox(state);
            const newInstance = stateA.instance.plus(durration);
            return [Object.assign(Object.assign({}, stateA), { instance: newInstance }), newInstance];
        });
    },
    subtract: (a, durration) => {
        return mkLuxonTimebox((state) => {
            const [stateA, val] = a.runBox(state);
            const newInstance = stateA.instance.minus(durration);
            return [Object.assign(Object.assign({}, stateA), { instance: newInstance }), newInstance];
        });
    },
    diff: (a, b) => {
        return mkLuxonTimebox((state) => {
            const [stateA] = a.runBox(state);
            const [stateB] = b.runBox(state);
            const diff = stateA.instance.diff(stateB.instance).milliseconds;
            const newInstance = state.luxon.DateTime.fromMillis(diff);
            return [Object.assign(Object.assign({}, stateA), { instance: newInstance }), diff];
        });
    },
    setLocale: (a, locale) => {
        return mkLuxonTimebox((state) => {
            const [s1, v1] = a.runBox(state);
            const newTime = s1.instance.setLocale(locale);
            return [Object.assign(Object.assign({}, s1), { instance: newTime }), newTime];
        });
    },
    convertTimezones: (a, newTz, opts = {}) => {
        return mkLuxonTimebox((state) => {
            const [s1, v1] = a.runBox(state);
            const newTimezone = legacyTimezoneSupport(s1, newTz);
            const newTime = s1.instance.setZone(newTimezone, {
                keepLocalTime: opts.keepLocalTime,
            });
            return [Object.assign(Object.assign({}, s1), { timeZone: newTimezone, instance: newTime }), newTime];
        });
    },
    convertTimezonesC: (newTz, opts = {}) => (a) => {
        return mkLuxonTimebox((state) => {
            const [s1, v1] = a.runBox(state);
            const newTimezone = legacyTimezoneSupport(s1, newTz);
            const newTime = s1.instance.setZone(newTimezone, {
                keepLocalTime: opts.keepLocalTime,
            });
            return [Object.assign(Object.assign({}, s1), { timeZone: newTimezone, instance: newTime }), newTime];
        });
    },
    format: (a, format) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = a.runBox(state);
            return [s1, s1.instance.toFormat(format)];
        });
    },
    formatC: (format) => (a) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = a.runBox(state);
            return [s1, s1.instance.toFormat(format)];
        });
    },
    year: (a) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = a.runBox(state);
            return [s1, s1.instance.year];
        });
    },
    month: (a) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = a.runBox(state);
            return [s1, s1.instance.month];
        });
    },
    day: (a) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = a.runBox(state);
            return [s1, s1.instance.day];
        });
    },
    localeWeekDay: (a, keepLocale = false) => {
        return mkLuxonTimebox((state) => {
            const [s1] = a.runBox(state);
            const weekDayLong = keepLocale ? s1.instance.weekdayLong : s1.instance.setLocale('en-US').weekdayLong;
            return [s1, weekDayLong];
        });
    },
    weekday: (a) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = a.runBox(state);
            const weekDay = s1.instance.weekday;
            return [s1, weekDay];
        });
    },
    weekdayName: (a) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = a.runBox(state);
            const weekDay = s1.instance.setLocale('en-US').weekdayLong;
            return [s1, weekDay];
        });
    },
    ordinal: (a) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = a.runBox(state);
            return [s1, s1.instance.ordinal];
        });
    },
    hour: (a) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = a.runBox(state);
            return [s1, s1.instance.hour];
        });
    },
    minute: (a) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = a.runBox(state);
            return [s1, s1.instance.minute];
        });
    },
    second: (a) => {
        return mkLuxonTimebox((state) => {
            const [s1] = a.runBox(state);
            return [s1, s1.instance.second];
        });
    },
    millisecond: (a) => {
        return mkLuxonTimebox((state) => {
            const [s1] = a.runBox(state);
            return [s1, s1.instance.millisecond];
        });
    },
    fromTimeString: (time, tz) => mkLuxonTimebox((state) => {
        const now = state.luxon.DateTime.now();
        const iso = `${now.toFormat('yyyy-MM-dd')}T${time}`;
        const timeZone = tz && legacyTimezoneSupport(state, tz);
        const instance = timeZone
            ? state.luxon.DateTime.fromISO(iso, { zone: timeZone })
            : state.luxon.DateTime.fromISO(iso);
        return [Object.assign(Object.assign({}, state), { instance }), instance];
    }),
    fromDateString: (date, tz) => mkLuxonTimebox((state) => {
        const timeZone = tz && legacyTimezoneSupport(state, tz);
        const now = state.luxon.DateTime.now().setZone(timeZone);
        const iso = `${date}T${now.toFormat('HH:mm')}`;
        const instance = timeZone
            ? state.luxon.DateTime.fromISO(iso, { zone: timeZone })
            : state.luxon.DateTime.fromISO(iso);
        return [Object.assign(Object.assign({}, state), { instance }), instance];
    }),
    fromDateTimeString: (date, time, tz) => mkLuxonTimebox((state) => {
        const iso = `${date}T${time}`;
        const timezone = tz && legacyTimezoneSupport(state, tz);
        const instance = timezone
            ? state.luxon.DateTime.fromISO(iso).setZone(timezone, {
                keepLocalTime: true,
            })
            : state.luxon.DateTime.fromISO(iso);
        return [Object.assign(Object.assign({}, state), { instance }), instance];
    }),
    set: (tb, props) => {
        return mkLuxonTimebox((state) => {
            const [s1, r1] = tb.runBox(state);
            return [Object.assign(Object.assign({}, s1), { instance: s1.instance.set(props) }), s1.instance.set(props)];
        });
    },
    valid: (tb) => {
        return mkLuxonTimebox((state) => {
            const [s1] = tb.runBox(state);
            return [s1, s1.instance.isValid];
        });
    },
    execMapTupple: (tb, fn) => {
        return fn(tb).map((v) => v.exec());
    },
    execMapObj: (tb, fn) => {
        return Object.entries(fn(tb)).reduce((acc, curr) => {
            const [key, val] = curr;
            return Object.assign(Object.assign({}, acc), { [key]: val.exec() });
        }, {});
    },
    result: result,
};
export class LuxonImpl extends TimeBox {
    constructor() {
        super(...arguments);
        this.run = () => impl.run(this);
        this.lt = (a) => impl.lt(this, a);
        this.lte = (a) => impl.lte(this, a);
        this.gt = (a) => impl.gt(this, a);
        this.gte = (a) => impl.gte(this, a);
        this.eq = (a) => impl.eq(this, a);
        this.add = (duration) => impl.add(this, duration);
        this.subtract = (duration) => impl.subtract(this, duration);
        this.diff = (tb) => impl.diff(this, tb);
        this.cond = (pred) => impl.cond(this, pred);
        this.toUtc = () => impl.toUtc(this);
        this.toJSDate = () => impl.toJsDate(this);
        this.convertTimezones = (timezone) => impl.convertTimezones(this, timezone);
        this.setLocale = (locale) => impl.setLocale(this, locale);
        this.format = (format) => impl.format(this, format);
        this.valid = () => impl.valid(this);
        this.unix = () => impl.unix(this);
        this.year = () => impl.year(this);
        this.month = () => impl.month(this);
        this.day = () => impl.day(this);
        this.weekday = () => impl.weekday(this);
        this.weekdayName = () => impl.weekdayName(this);
        this.ordinal = () => impl.ordinal(this);
        this.hour = () => impl.hour(this);
        this.minute = () => impl.minute(this);
        this.second = () => impl.second(this);
        this.millisecond = () => impl.millisecond(this);
        this.exec = () => impl.run(this)[1];
        this.execMapTupple = (fn) => impl.execMapTupple(this, fn);
        this.execMapObj = (fn) => impl.execMapObj(this, fn);
        this.eval = () => impl.run(this)[0];
        this.localeWeekDay = () => impl.localeWeekDay(this);
        this.toJsDate = () => impl.toJsDate(this);
        this.toISO = () => impl.toISO(this);
        this.convertRelative = (tzFrom, tzTo) => {
            return impl.convertTimezones(this, tzFrom, { keepLocalTime: true }).convertTimezones(tzTo);
        };
        this.convertRelativeToUtc = (tzFrom) => {
            return impl.convertTimezones(this, tzFrom, { keepLocalTime: true }).convertTimezones('utc');
        };
        this.set = (props) => impl.set(this, props);
    }
}
LuxonImpl.functional = impl;
LuxonImpl.utcNow = impl.utcNow;
LuxonImpl.now = impl.now;
LuxonImpl.realNow = impl.realNow;
LuxonImpl.fromJsObject = impl.fromJsObject;
LuxonImpl.fromISO = impl.fromISO;
LuxonImpl.fromMillis = impl.fromMillis;
LuxonImpl.fromFormat = (dateTime, format) => mkLuxonTimebox((state) => {
    const m = state.luxon.DateTime.fromFormat(dateTime, format);
    return [Object.assign(Object.assign({}, state), { instance: m }), m];
});
LuxonImpl.fromTimeString = (time, timeZone) => impl.fromTimeString(time, timeZone);
LuxonImpl.fromDateString = (date, timeZone) => impl.fromDateString(date, timeZone);
LuxonImpl.fromDateTimeString = (date, time, timezone) => impl.fromDateTimeString(date, time, timezone);
LuxonImpl.convertRelativeString = (time, tzFrom, tzTo) => {
    return impl.pipe(impl.fromTimeString(time), impl.convertTimezonesC(tzFrom, { keepLocalTime: true }), impl.convertTimezonesC(tzTo));
};
LuxonImpl.convertRelativeStringToUtc = (time, tzFrom) => {
    return impl.pipe(impl.fromTimeString(time), impl.convertTimezonesC(tzFrom, { keepLocalTime: true }), impl.convertTimezonesC('UTC'));
};
LuxonImpl.convertRelativeStringDate = (time, tzFrom, tzTo) => {
    return impl.pipe(impl.fromDateString(time), impl.convertTimezonesC(tzFrom, { keepLocalTime: true }), impl.convertTimezonesC(tzTo));
};
export default impl;
